import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Loader, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, RefreshCw, FileText, Brain, Sparkles, CheckCircle2, AlertTriangle, ChevronDown } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { getOpaEvolution, getOpaAttendantAttendances, getAttendantAIAnalysis, triggerAttendantAIAnalysis } from '../../services/apiService';
import { db } from '../../firebaseConfig';
import { collection, doc, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { exportAttendantPDF } from '../../utils/attendantPdfExport';
import OpaAvatar from './OpaAvatar';


// Divide o comentário original em [antes, trecho, depois] para destacar em negrito a parte
// que a IA apontou como exemplo do ponto identificado (o trecho é sempre uma substring literal validada no backend)
const renderHighlightedComment = (comentario, trecho, highlightClassName) => {
    if (!trecho) return comentario;
    const idx = comentario.indexOf(trecho);
    if (idx === -1) return comentario;
    return (
        <>
            {comentario.substring(0, idx)}
            <strong className={highlightClassName}>{trecho}</strong>
            {comentario.substring(idx + trecho.length)}
        </>
    );
};

const AttendantModal = ({ attendant, sectorsList, onClose, theme, month, year }) => {
    const isLegacyMethodology = attendant?.methodologyVersion === 'legacy-cache';
    const normalizeScore = (score) => {
        const parsedScore = Number(score);
        if (!Number.isFinite(parsedScore)) return 3;
        return Math.min(5, Math.max(1, Math.round(parsedScore)));
    };

    // Formata uma data de forma segura, suportando strings ISO, timestamps do Firestore ou objetos Date
    const formatDate = (dateVal) => {
        if (!dateVal) return '';
        if (typeof dateVal === 'object' && dateVal.seconds) {
            return new Date(dateVal.seconds * 1000).toLocaleDateString('pt-BR');
        }
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
    };

    const [range, setRange] = useState('3months');
    const [evolutionData, setEvolutionData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [loadingAi, setLoadingAi] = useState(false);
    const [syncingAi, setSyncingAi] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    const [rankingGroup, setRankingGroup] = useState(attendant?.rankingProfile || 'standard');
    const [savingSettings, setSavingSettings] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isExamplesOpen, setIsExamplesOpen] = useState(false);

    // Escuta em tempo real as configurações deste atendente
    useEffect(() => {
        if (!attendant?.id) return;
        const unsubscribe = onSnapshot(doc(db, 'attendant_settings', attendant.id), (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                setRankingGroup(data.rankingGroup || attendant?.rankingProfile || 'standard');
            } else {
                setRankingGroup(attendant?.rankingProfile || 'standard');
            }
        });
        return () => unsubscribe();
    }, [attendant]);

    const handleRankingGroupChange = async (value) => {
        if (savingSettings) return;
        setRankingGroup(value);
        setSavingSettings(true);
        try {
            await setDoc(doc(db, 'attendant_settings', attendant.id), {
                rankingGroup: value,
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            console.error('Erro ao salvar configurações do atendente:', error);
            alert('Não foi possível salvar a configuração.');
            setRankingGroup(attendant?.rankingProfile || 'standard');
        } finally {
            setSavingSettings(false);
        }
    };

    const localFinalScore = attendant.finalScore;

    useEffect(() => {
        if (!attendant) return;
        fetchEvolution();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendant, range]);

    useEffect(() => {
        if (!attendant) return;
        fetchAiAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendant]);

    const fetchEvolution = async () => {
        setLoading(true);
        try {
            const data = await getOpaEvolution(attendant.id, range);
            if (data && data.success) {
                setEvolutionData(data.evolution);
            }
        } catch (error) {
            console.error('Erro ao buscar evolução', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAiAnalysis = async () => {
        setLoadingAi(true);
        try {
            const res = await getAttendantAIAnalysis(attendant.id);
            if (res && res.success) {
                setAiAnalysis(res.analysis);
            } else {
                setAiAnalysis(null);
            }
        } catch (err) {
            console.error("Erro ao buscar análise de IA:", err);
            setAiAnalysis(null);
        } finally {
            setLoadingAi(false);
        }
    };

    const handleExportPdf = async () => {
        if (exportingPdf) return;
        setExportingPdf(true);
        try {
            await exportAttendantPDF(
                attendant,
                observations,
                aiAnalysis,
                'attendant-evolution-chart',
                sectorsList,
                { month, year },
            );
        } catch (err) {
            console.error('Erro ao exportar PDF:', err);
            alert('Erro ao gerar relatório em PDF.');
        } finally {
            setExportingPdf(false);
        }
    };


    const [observations, setObservations] = useState([]);
    const [showObservationForm, setShowObservationForm] = useState(false);
    const [editingObservation, setEditingObservation] = useState(null);

    // Form fields
    const [obsProtocol, setObsProtocol] = useState('');
    const [obsDate, setObsDate] = useState('');
    const [obsScore, setObsScore] = useState('');
    const [obsComment, setObsComment] = useState('');
    const [submittingObs, setSubmittingObs] = useState(false);

    // Sincronização OPA
    const [syncingOpa, setSyncingOpa] = useState(false);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Comentário expandido
    const [expandedCommentId, setExpandedCommentId] = useState(null);

    useEffect(() => {
        if (!attendant) return;
        const q = query(
            collection(db, 'attendant_observations'),
            where('attendantId', '==', attendant.id),
            orderBy('date', 'desc')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const obs = [];
            snapshot.forEach((doc) => {
                obs.push({ id: doc.id, ...doc.data() });
            });
            setObservations(obs);
        });
        return () => unsubscribe();
    }, [attendant]);

    const handleOpenObservationForm = (obs = null) => {
        if (obs) {
            setEditingObservation(obs);
            setObsProtocol(obs.protocol || '');
            setObsDate(obs.date || '');
            setObsScore(String(normalizeScore(obs.score)));
            setObsComment(obs.comment || '');
        } else {
            setEditingObservation(null);
            setObsProtocol('');
            setObsDate(new Date().toISOString().split('T')[0]);
            setObsScore('3');
            setObsComment('');
        }
        setShowObservationForm(true);
    };

    const handleSaveObservation = async (e) => {
        e.preventDefault();
        const normalizedScore = normalizeScore(obsScore);
        setSubmittingObs(true);
        try {
            const obsData = {
                attendantId: attendant.id,
                protocol: obsProtocol,
                date: obsDate,
                score: normalizedScore,
                comment: obsComment,
                updatedAt: serverTimestamp()
            };
            if (editingObservation) {
                await updateDoc(doc(db, 'attendant_observations', editingObservation.id), obsData);
            } else {
                obsData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'attendant_observations'), obsData);
            }
            setShowObservationForm(false);
        } catch (error) {
            console.error('Erro ao salvar observação', error);
            alert('Falha ao salvar observação.');
        } finally {
            setSubmittingObs(false);
        }
    };

    const handleDeleteObservation = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta observação?')) return;
        try {
            await deleteDoc(doc(db, 'attendant_observations', id));
        } catch (error) {
            console.error('Erro ao excluir observação', error);
            alert('Falha ao excluir observação.');
        }
    };

    const handleSyncFromOpa = async () => {
        if (syncingOpa) return;
        setSyncingOpa(true);
        try {
            const now = new Date();
            const syncMonth = month || (now.getMonth() + 1);
            const syncYear = year || now.getFullYear();
            const data = await getOpaAttendantAttendances(attendant.id, syncMonth, syncYear);
            if (!data || !data.success) {
                alert('Não foi possível buscar os atendimentos do OPA.');
                return;
            }

            const attendances = data.attendances || [];
            if (attendances.length === 0) {
                alert('Nenhum atendimento finalizado encontrado para este atendente no período.');
                return;
            }

            // Limpeza rigorosa: Apagar registros antigos do OPA que não são de IA ou não têm nota
            const obsToDelete = observations.filter(o => {
                if (o.source !== 'opa_sync') return false; // Mantém os inseridos manualmente
                if (o.score === null || o.score === undefined || o.score === '') return true;
                if (!o.comment || !o.comment.includes('[IA]')) return true; // Se não tem a marca da IA, exclui
                return false;
            });

            for (const obs of obsToDelete) {
                if (obs.id) {
                    try {
                        await deleteDoc(doc(db, 'attendant_observations', obs.id));
                    } catch (err) {
                        console.error('Erro ao apagar observação antiga:', err);
                    }
                }
            }

            // O Set de protocolos existentes precisa ignorar os que acabamos de apagar
            const deletedIds = new Set(obsToDelete.map(o => o.id));
            const existingProtocols = new Set(
                observations.filter(o => !deletedIds.has(o.id)).map(o => o.protocol)
            );
            const newObservations = [];

            for (const att of attendances) {
                const protocol = att.protocol || att.id;
                if (!protocol || existingProtocols.has(protocol)) continue;

                // Regra estrita: APENAS atendimentos com avaliação de IA
                const hasAiEvaluation = (att.aiScore !== null && att.aiScore !== undefined) || !!att.aiObservation;
                if (!hasAiEvaluation) continue;

                const score = att.aiScore ?? null;

                // Pega APENAS as observações da IA, ignorando comentários de clientes ou manuais do OPA
                const commentParts = [];
                if (att.aiObservation) commentParts.push(`[IA] ${att.aiObservation}`);
                
                const comment = commentParts.join(' | ') || `[IA] Avaliação de IA sem observação textual.`;

                const dateStr = att.date ? att.date.split('T')[0] : new Date().toISOString().split('T')[0];

                newObservations.push({
                    attendantId: attendant.id,
                    protocol: String(protocol).substring(0, 40),
                    date: dateStr,
                    score: score !== null ? Math.min(Math.max(Number(score.toFixed(1)), 0), 5) : null,
                    comment: comment.substring(0, 1000),
                    source: 'opa_sync',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
            }

            if (newObservations.length === 0) {
                alert('Todos os atendimentos deste período já foram registrados como observações.');
                return;
            }

            let added = 0;
            for (const obsData of newObservations) {
                try {
                    await addDoc(collection(db, 'attendant_observations'), obsData);
                    added++;
                } catch (err) {
                    console.error('Erro ao inserir observação:', err);
                }
            }

            // Acionar análise da IA automaticamente após a sincronização
            setSyncingAi(true);
            try {
                const res = await triggerAttendantAIAnalysis(attendant.id, attendant.finalScore);
                if (res && res.success) {
                    setAiAnalysis(res.analysis);
                }
            } catch (err) {
                console.error('Erro ao acionar análise da IA:', err);
            } finally {
                setSyncingAi(false);
            }

            alert(`${added} atendimento(s) importado(s) do OPA e análise de IA atualizada com sucesso!`);
        } catch (error) {
            console.error('Erro ao sincronizar com OPA:', error);
            alert('Erro ao sincronizar com OPA. Tente novamente.');
        } finally {
            setSyncingOpa(false);
        }
    };

    const isDark = theme === 'dark' || document.documentElement.classList.contains('dark');

    if (!attendant) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <OpaAvatar name={attendant.name} fotoUrl={attendant.fotoUrl} className="h-12 w-12 text-xl border border-slate-200 dark:border-slate-700" />
                            {attendant.isTop3 && (
                                <div className="absolute -top-2 -right-2 text-xl filter drop-shadow-md">
                                    {attendant.actualRank === 0 ? '🥇' : attendant.actualRank === 1 ? '🥈' : '🥉'}
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">{attendant.name}</h2>
                                <span className="text-sm font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                                    {attendant.isProvisional ? 'Provisório' : `${attendant.rank}º Lugar`}
                                </span>
                                <span className="text-sm font-black px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                                    Nota: {localFinalScore?.toFixed(2)}
                                </span>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Detalhes e Evolução</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                        aria-label="Fechar"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                        {[
                            { label: isLegacyMethodology ? 'Avaliação' : 'Qualidade ajustada', score: attendant.qualityScore, weight: isLegacyMethodology ? 40 : 50, contribution: attendant.contributions?.quality, color: 'text-amber-600 dark:text-amber-400' },
                            { label: isLegacyMethodology ? 'Volume' : 'Produtividade', score: attendant.productivityScore, weight: 35, contribution: attendant.contributions?.productivity, color: 'text-emerald-600 dark:text-emerald-400' },
                            { label: isLegacyMethodology ? 'TMA' : 'Eficiência', score: attendant.efficiencyScore, weight: isLegacyMethodology ? 25 : 15, contribution: attendant.contributions?.efficiency, color: 'text-sky-600 dark:text-sky-400' },
                        ].map(component => (
                            <div key={component.label} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-3">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{component.label} • {component.weight}%</p>
                                <div className="flex items-end justify-between mt-1">
                                    <span className={`text-xl font-black ${component.color}`}>{component.score?.toFixed(2) || '-'}</span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">+{component.contribution?.toFixed(2) || '0.00'} na final</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {attendant.qualityGateApplied && (
                        <div className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/20 p-3 text-xs text-red-700 dark:text-red-300">
                            <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
                            A qualidade ajustada ficou abaixo do mínimo. Por isso, produtividade alta não pode elevar a nota final acima do teto de segurança.
                        </div>
                    )}
                    {/* Grupo comparável do atendente */}
                    <div className="bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700/80 rounded-2xl mb-6 overflow-hidden transition-all duration-200 hover:shadow-sm">
                        <button
                            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                            className="w-full flex items-center justify-between p-4 focus:outline-none select-none text-left"
                        >
                            <div className="flex items-center gap-2.5">
                                <Sparkles className="w-5 h-5 text-purple-500" />
                                <div>
                                    <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Grupo de comparação</h3>
                                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                        {attendant.comparisonGroupLabel || 'Atendimento por setor'}
                                    </span>
                                </div>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isSettingsOpen ? 'rotate-180' : ''}`} />
                        </button>

                        <div className={`transition-all duration-300 ease-in-out ${isSettingsOpen ? 'max-h-[500px] border-t border-slate-200 dark:border-slate-700/80 p-4 sm:p-5' : 'max-h-0 opacity-0 pointer-events-none'}`} style={{ display: isSettingsOpen ? 'block' : 'none' }}>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
                                Todos do mesmo grupo usam exatamente os mesmos pesos. Use um grupo separado para supervisão ou atividade especializada; a nova classificação aparece após sincronizar o ranking.
                            </p>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
                                Perfil de trabalho
                                <select
                                    value={rankingGroup}
                                    disabled={savingSettings}
                                    onChange={(event) => handleRankingGroupChange(event.target.value)}
                                    className="mt-2 w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none disabled:opacity-60"
                                >
                                    <option value="standard">Atendimento</option>
                                    <option value="supervision">Supervisão</option>
                                    <option value="specialized">Atividade especializada</option>
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* Filtro do Gráfico */}
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-blue-500" />
                            Evolução Semanal
                        </h3>
                        <select
                            value={range}
                            onChange={(e) => setRange(e.target.value)}
                            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        >
                            <option value="currentMonth">Mês Atual</option>
                            <option value="lastMonth">Mês Anterior</option>
                            <option value="3months">3 Meses</option>
                            <option value="6months">6 Meses</option>
                            <option value="1year">1 Ano</option>
                        </select>
                    </div>

                    {/* Gráfico */}
                    <div id="attendant-evolution-chart" className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 mb-8 border border-slate-200 dark:border-slate-700 h-80">
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader className="w-8 h-8 text-blue-500 animate-spin" />
                            </div>
                        ) : evolutionData.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
                                Sem dados para o período selecionado.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                                <AreaChart data={evolutionData}>
                                    <defs>
                                        <linearGradient id="colorTmaScore" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.18}/>
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorEvalScore" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.16}/>
                                            <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorVolumeScore" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.16}/>
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorFinalScore" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#a855f7" stopOpacity={0.14}/>
                                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                                    <XAxis
                                        dataKey="weekLabel"
                                        stroke={isDark ? '#94a3b8' : '#64748b'}
                                        fontSize={12}
                                    />
                                    <YAxis
                                        domain={[0, 5]}
                                        stroke={isDark ? '#94a3b8' : '#64748b'}
                                        fontSize={12}
                                    />
                                    <RechartsTooltip
                                        cursor={{ stroke: isDark ? '#cbd5e1' : '#64748b', strokeWidth: 1, strokeDasharray: '4 4' }}
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                            borderColor: isDark ? '#334155' : '#e2e8f0',
                                            color: isDark ? '#f8fafc' : '#0f172a',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                                    <Area type="monotone" dataKey="tmaScore" name="Eficiência" stroke="#38bdf8" strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 2 }} fillOpacity={1} fill="url(#colorTmaScore)" />
                                    <Area type="monotone" dataKey="evalScore" name="Qualidade ajustada" stroke="#f59e0b" strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 2 }} fillOpacity={1} fill="url(#colorEvalScore)" />
                                    <Area type="monotone" dataKey="volumeScore" name="Produtividade" stroke="#22c55e" strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 2 }} fillOpacity={1} fill="url(#colorVolumeScore)" />
                                    <Area type="monotone" dataKey="finalScore" name="Nota Final" stroke="#a855f7" strokeWidth={4} dot={false} activeDot={{ r: 6, strokeWidth: 2 }} fillOpacity={1} fill="url(#colorFinalScore)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* Seção de Análise de IA */}
                    <div className="mt-8 border-t border-slate-200 dark:border-slate-700 pt-8">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                <Brain className="w-5 h-5 text-indigo-500" />
                                Análise de Performance por IA
                            </h3>
                            {aiAnalysis && (aiAnalysis.updatedAt || aiAnalysis.analyzedAt) && (
                                <span className="text-xs text-slate-400 dark:text-slate-500">
                                    Gerada em: {formatDate(aiAnalysis.updatedAt || aiAnalysis.analyzedAt)}
                                </span>
                            )}
                        </div>

                        {loadingAi ? (
                            <div className="flex items-center justify-center py-10 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <div className="flex flex-col items-center gap-3">
                                    <Loader className="w-8 h-8 text-indigo-500 animate-spin" />
                                    <p className="text-sm text-slate-500 dark:text-slate-400">Carregando diagnóstico da inteligência artificial...</p>
                                </div>
                            </div>
                        ) : syncingAi ? (
                            <div className="flex items-center justify-center py-12 bg-indigo-50/30 dark:bg-indigo-950/10 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 animate-pulse">
                                <div className="flex flex-col items-center gap-3 text-center px-4">
                                    <div className="p-3 bg-indigo-100 dark:bg-indigo-900/50 rounded-full text-indigo-600 dark:text-indigo-400 animate-bounce">
                                        <Sparkles className="w-6 h-6" />
                                    </div>
                                    <h4 className="font-bold text-indigo-950 dark:text-indigo-200">A Inteligência Artificial está analisando as avaliações...</h4>
                                    <p className="text-xs text-indigo-700/80 dark:text-indigo-400/80 max-w-md">
                                        Selecionando amostras representativas dos últimos 3 meses, processando feedbacks e calculando o score de qualidade. Isso pode levar alguns segundos.
                                    </p>
                                </div>
                            </div>
                        ) : !aiAnalysis ? (
                            <div className="p-6 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-slate-200 dark:border-slate-800 text-center flex flex-col items-center justify-center">
                                <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-400 dark:text-slate-500 mb-3">
                                    <Brain className="w-6 h-6" />
                                </div>
                                <h4 className="font-bold text-slate-700 dark:text-slate-300 mb-1">Sem análise de IA recente</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mb-4">
                                    Para gerar o diagnóstico de pontos fortes, fracos e o score de performance da IA com base nas avaliações dos últimos 3 meses, clique em "Sincronizar do OPA".
                                </p>
                            </div>
                        ) : (() => {
                            const getAiScoreColor = (score) => {
                                const val = Number(score) || 0;
                                if (val >= 80) {
                                    return {
                                        stroke: 'stroke-emerald-500 dark:stroke-emerald-400',
                                        text: 'text-emerald-600 dark:text-emerald-400',
                                        bg: 'from-emerald-50/50 to-teal-50/50 dark:from-slate-900/50 dark:to-slate-800/50 border-emerald-100/80 dark:border-emerald-900/20'
                                    };
                                }
                                if (val >= 50) {
                                    return {
                                        stroke: 'stroke-amber-500 dark:stroke-amber-400',
                                        text: 'text-amber-600 dark:text-amber-400',
                                        bg: 'from-amber-50/50 to-orange-50/50 dark:from-slate-900/50 dark:to-slate-800/50 border-amber-100/80 dark:border-amber-900/20'
                                    };
                                }
                                return {
                                    stroke: 'stroke-rose-500 dark:stroke-rose-400',
                                    text: 'text-rose-600 dark:text-rose-400',
                                    bg: 'from-rose-50/50 to-red-50/50 dark:from-slate-900/50 dark:to-slate-800/50 border-rose-100/80 dark:border-rose-900/20'
                                };
                            };
                            const scoreColors = getAiScoreColor(aiAnalysis.scoreIA);

                            return (
                                <div className="flex flex-col gap-6">
                                    {/* Card do Score e Resumo (Diagnóstico Geral) */}
                                    <div className={`bg-gradient-to-br ${scoreColors.bg} p-6 rounded-2xl border flex flex-col md:flex-row items-center gap-6 relative overflow-hidden`}>
                                        {/* Círculo do Score */}
                                        <div className="flex flex-col items-center text-center justify-center flex-shrink-0">
                                            <div className="relative flex items-center justify-center mb-2">
                                                <svg className="w-28 h-28 transform -rotate-90">
                                                    <circle
                                                        cx="56"
                                                        cy="56"
                                                        r="46"
                                                        className="stroke-slate-200 dark:stroke-slate-800"
                                                        strokeWidth="8"
                                                        fill="transparent"
                                                    />
                                                    <circle
                                                        cx="56"
                                                        cy="56"
                                                        r="46"
                                                        className={`${scoreColors.stroke} transition-all duration-1000 ease-out`}
                                                        strokeWidth="8"
                                                        fill="transparent"
                                                        strokeDasharray={2 * Math.PI * 46}
                                                        strokeDashoffset={2 * Math.PI * 46 * (1 - aiAnalysis.scoreIA / 100)}
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                                <div className="absolute flex flex-col items-center justify-center">
                                                    <span className={`text-3xl font-black ${scoreColors.text}`}>{aiAnalysis.scoreIA}%</span>
                                                    <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500 tracking-wider">Score IA</span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Diagnóstico Geral */}
                                        <div className="flex-1 text-center md:text-left">
                                            <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Diagnóstico Geral</h4>
                                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed italic">
                                                "{aiAnalysis.resumo}"
                                            </p>
                                        </div>
                                    </div>

                                    {/* Pontos Fortes e Oportunidades de Melhoria */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Pontos Fortes */}
                                    <div className="bg-emerald-50/50 dark:bg-emerald-950/10 p-5 rounded-2xl border border-emerald-100/50 dark:border-emerald-900/20">
                                        <h4 className="text-sm font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-2 mb-3">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                            Pontos Fortes
                                        </h4>
                                        <ul className="space-y-2">
                                            {aiAnalysis.pontosFortes && aiAnalysis.pontosFortes.map((pf, idx) => (
                                                <li key={idx} className="flex gap-2 text-xs text-slate-700 dark:text-slate-300 align-top">
                                                    <span className="text-emerald-500 select-none">•</span>
                                                    <span>{pf}</span>
                                                </li>
                                            ))}
                                            {(!aiAnalysis.pontosFortes || aiAnalysis.pontosFortes.length === 0) && (
                                                <li className="text-xs text-slate-400 italic">Nenhum ponto forte identificado.</li>
                                            )}
                                        </ul>
                                    </div>

                                    {/* Oportunidades de Melhoria */}
                                    <div className="bg-rose-50/50 dark:bg-rose-950/10 p-5 rounded-2xl border border-rose-100/50 dark:border-rose-900/20">
                                        <h4 className="text-sm font-bold text-rose-800 dark:text-rose-300 flex items-center gap-2 mb-3">
                                            <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                                            Oportunidades de Melhoria
                                        </h4>
                                        <ul className="space-y-2">
                                            {aiAnalysis.pontosFracos && aiAnalysis.pontosFracos.map((pd, idx) => (
                                                <li key={idx} className="flex gap-2 text-xs text-slate-700 dark:text-slate-300 align-top">
                                                    <span className="text-rose-500 select-none">•</span>
                                                    <span>{pd}</span>
                                                </li>
                                            ))}
                                            {(!aiAnalysis.pontosFracos || aiAnalysis.pontosFracos.length === 0) && (
                                                <li className="text-xs text-slate-400 italic">Nenhuma oportunidade identificada.</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>

                                {/* Exemplos de Atendimentos (do pior para o melhor) */}
                                {aiAnalysis.exemplosAtendimentos && aiAnalysis.exemplosAtendimentos.length > 0 && (
                                    <div className="bg-slate-50 dark:bg-slate-900/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                        <button
                                            type="button"
                                            onClick={() => setIsExamplesOpen(!isExamplesOpen)}
                                            aria-expanded={isExamplesOpen}
                                            aria-controls="attendant-ai-examples"
                                            className="w-full flex items-center justify-between gap-3 text-left"
                                        >
                                            <span className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300">
                                                <Sparkles className="w-4 h-4 text-indigo-500" />
                                                Exemplos de Atendimentos
                                                <span className="text-xs font-medium text-slate-400 dark:text-slate-500">({aiAnalysis.exemplosAtendimentos.length})</span>
                                            </span>
                                            <ChevronDown className={`w-5 h-5 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isExamplesOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        {isExamplesOpen && <div id="attendant-ai-examples" className="mt-4">
                                            <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">
                                                Amostras identificadas pela IA, ordenadas do pior para o melhor atendimento no período avaliado.
                                            </p>
                                            <ul className="space-y-3">
                                            {aiAnalysis.exemplosAtendimentos.map((ex, idx) => {
                                                const isRuim = ex.tipo === 'ruim';
                                                return (
                                                    <li
                                                        key={`${ex.protocolo}-${idx}`}
                                                        className={`p-4 rounded-xl border text-xs ${isRuim
                                                            ? 'bg-rose-50/50 dark:bg-rose-950/10 border-rose-100/50 dark:border-rose-900/20'
                                                            : 'bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-100/50 dark:border-emerald-900/20'}`}
                                                    >
                                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isRuim
                                                                ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                                                                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}
                                                            >
                                                                {isRuim ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                                                                {isRuim ? 'Mau atendimento' : 'Bom atendimento'}
                                                            </span>
                                                            <span className="font-semibold text-slate-600 dark:text-slate-300">Protocolo: {ex.protocolo}</span>
                                                            {ex.data && <span className="text-slate-400 dark:text-slate-500">{formatDate(ex.data)}</span>}
                                                            {ex.nota !== null && ex.nota !== undefined && (
                                                                <span className="text-slate-400 dark:text-slate-500">Nota: {ex.nota}</span>
                                                            )}
                                                        </div>
                                                        <p className="text-slate-700 dark:text-slate-300 font-medium mb-1">{ex.motivo}</p>
                                                        {ex.comentario && (
                                                            <p className="text-slate-500 dark:text-slate-400 italic mb-2">
                                                                "{renderHighlightedComment(
                                                                    ex.comentario,
                                                                    ex.trechoDestaque,
                                                                    isRuim
                                                                        ? 'font-bold not-italic text-rose-700 dark:text-rose-400'
                                                                        : 'font-bold not-italic text-emerald-700 dark:text-emerald-400'
                                                                )}"
                                                            </p>
                                                        )}
                                                        {ex.tags && ex.tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                                {ex.tags.map((tag, tagIdx) => (
                                                                    <span
                                                                        key={tagIdx}
                                                                        className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200/70 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                                                    >
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                            </ul>
                                        </div>}
                                    </div>
                                )}
                            </div>
                            );
                        })()}
                    </div>

                    {/* Observações */}
                    <div className="mt-8 border-t border-slate-200 dark:border-slate-700 pt-8">
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                Observações sobre Atendimentos
                            </h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleExportPdf}
                                    disabled={exportingPdf}
                                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                    title="Exportar relatório completo em PDF"
                                >
                                    {exportingPdf ? (
                                        <Loader className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <FileText className="w-4 h-4" />
                                    )}
                                    {exportingPdf ? 'Exportando...' : 'Exportar PDF'}
                                </button>
                                <button
                                    onClick={handleSyncFromOpa}
                                    disabled={syncingOpa || syncingAi}
                                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                    title="Importar atendimentos finalizados do OPA Suite e atualizar análise da IA"
                                >
                                    <RefreshCw className={`w-4 h-4 ${syncingOpa || syncingAi ? 'animate-spin' : ''}`} />
                                    {syncingOpa ? 'Sincronizando...' : syncingAi ? 'Processando IA...' : 'Sincronizar do OPA'}
                                </button>
                                <button
                                    onClick={() => handleOpenObservationForm()}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                                <thead className="bg-slate-50 dark:bg-slate-900/50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Protocolo</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Data</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Nota</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Comentários</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                                    {observations.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center text-sm text-slate-500 dark:text-slate-400">
                                                Nenhuma observação registrada.
                                            </td>
                                        </tr>
                                    ) : (
                                        observations.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((obs) => {
                                            const isExpanded = expandedCommentId === obs.id;
                                            const isLongComment = obs.comment && obs.comment.length > 80;
                                            const isSynced = obs.source === 'opa_sync';

                                            return (
                                                <tr
                                                    key={obs.id}
                                                    className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer ${isSynced ? 'bg-emerald-50/40 dark:bg-emerald-900/10' : ''}`}
                                                    onClick={() => setExpandedCommentId(isExpanded ? null : obs.id)}
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                                                        <div className="flex items-center gap-2">
                                                            {obs.protocol}
                                                            {isSynced && (
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                                                    OPA
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{obs.date.split('-').reverse().join('/')}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                                        {obs.score !== null && obs.score !== undefined ? obs.score : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 max-w-xs">
                                                        {isExpanded || !isLongComment ? (
                                                            <div className="whitespace-pre-wrap break-words">{obs.comment}</div>
                                                        ) : (
                                                            <div>
                                                                {obs.comment.substring(0, 80)}...
                                                                <span className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 ml-1 font-medium cursor-pointer">
                                                                    ver mais
                                                                </span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleOpenObservationForm(obs); }}
                                                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteObservation(obs.id); }}
                                                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginação */}
                        {observations.length > itemsPerPage && (
                            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                    Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a <span className="font-medium">{Math.min(currentPage * itemsPerPage, observations.length)}</span> de <span className="font-medium">{observations.length}</span> resultados
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="p-1 rounded-md text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Página {currentPage} de {Math.ceil(observations.length / itemsPerPage)}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(observations.length / itemsPerPage)))}
                                        disabled={currentPage === Math.ceil(observations.length / itemsPerPage)}
                                        className="p-1 rounded-md text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}
                                        </div>
                </div>
            </div>

            {/* Modal de Formulário de Observação */}
            {showObservationForm && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-4 border-b border-slate-200 dark:border-slate-700">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                                {editingObservation ? 'Editar Observação' : 'Nova Observação'}
                            </h3>
                            <button
                                onClick={() => setShowObservationForm(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveObservation} className="p-4 flex flex-col gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Protocolo</label>
                                <input
                                    type="text"
                                    required
                                    maxLength={40}
                                    value={obsProtocol}
                                    onChange={(e) => setObsProtocol(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Data</label>
                                    <input
                                        type="date"
                                        required
                                        value={obsDate}
                                        onChange={(e) => setObsDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Nota</label>
                                        <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{obsScore}</span>
                                    </div>
                                    <input
                                        type="range"
                                        step="1"
                                        min="1"
                                        max="5"
                                        required
                                        value={obsScore}
                                        onChange={(e) => setObsScore(e.target.value)}
                                        className="w-full h-2 mt-4 accent-blue-600 cursor-pointer"
                                    />
                                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-2">
                                        <span>1</span>
                                        <span>2</span>
                                        <span>3</span>
                                        <span>4</span>
                                        <span>5</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Comentários</label>
                                <textarea
                                    required
                                    rows="3"
                                    maxLength={1000}
                                    value={obsComment}
                                    onChange={(e) => setObsComment(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                ></textarea>
                                <div className="text-right text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {obsComment.length}/1000
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowObservationForm(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingObs}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {submittingObs ? 'Salvando...' : 'Salvar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>,

        document.body
    );
};

export default AttendantModal;
